/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ExtensionQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/


import { Phone } from 'lucide-react';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';
import { useNavigate } from 'react-router-dom';

interface Props {
    isOpen: boolean;
    currentExtensionId?: string;
    extensions: any[];
    isLoadingInitial: boolean;
    isSearching: boolean;
    searchValue: string;
    onSearchChange: (v: string) => void;
    onClose: () => void;
}

export function ExtensionQuickListModal({
    isOpen, currentExtensionId, extensions, isLoadingInitial,
    isSearching, searchValue, onSearchChange, onClose,
}: Props) {
    const { t } = useI18n();
    const navigate = useNavigate();

    const handleSelect = (ext: any) => {
        onClose();
        navigate(`/pbx/extensions/extensions/${ext.uuid || ext.id}`);
    };

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('extensions.actions.quickList')}
            items={extensions}
            isLoadingInitial={isLoadingInitial}
            isSearching={isSearching}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onItemSelect={handleSelect}
            currentItemId={currentExtensionId}
            renderItem={(ext, isCurrent) => {
                const name = ext.name || ext.contact_email || '';
                return (
                    <button
                        onClick={() => { if (!isCurrent) handleSelect(ext); }}
                        disabled={isCurrent}
                        className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}
                    >
                        <div className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">{ext.extension}</span>
                                    {isCurrent && <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">{t('extensions.current')}</span>}
                                </div>
                                {name && <div className="text-sm text-muted-foreground truncate">{name}</div>}
                            </div>
                        </div>
                    </button>
                );
            }}
            searchPlaceholder={t('extensions.quickListSearch')}
            minCharsMessage={t('extensions.quickListMinChars')}
            emptyMessage={t('extensions.quickListPrompt')}
            noResultsMessage={t('extensions.quickListNoResults')}
            headerIcon={<Phone className="h-5 w-5" />}
            minSearchChars={2}
        />
    );
}
